// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { booleanQuerySchema, isoDateSchema, isoDateTimeSchema, paginationQuerySchema, timeSchema, uuidSchema } from '../common.js';
import { ATTENDANCE_NOTE_CATEGORIES, ATTENDANCE_NOTE_STATUSES, GEOFENCE_ENFORCEMENTS, GEOFENCE_SCOPES, NOTE_REVIEW_DECISIONS, REGULARISATION_STATUSES, REGULARISATION_TYPES, SELF_PUNCH_CHANNELS, SELF_PUNCH_DIRECTIONS, SELFIE_CHECKIN_STATUSES, SHIFT_SWAP_STATUSES, } from '../enums.js';
import { updateSchemaOf } from '../dto-features/devices.js';
// Employee portal attendance (HR portal Prompt 4): check-in / check-out with geofence verdicts, selfie check-ins, per-day
// reasons (notes), regularisation requests, the shift tab + swaps and self statistics — plus the manager / HR surfaces that
// review them (notes, selfies, grants, geofences). Every /orgs/:orgId/me/… endpoint acts on the caller's own employee
// record (the membership's employee link) and never accepts an employee id.
const latitudeSchema = z.number().min(-90).max(90);
const longitudeSchema = z.number().min(-180).max(180);
/** GPS accuracy radius in metres as the device reports it (0 = unknown precision is not accepted: omit it instead). */
const accuracySchema = z.number().positive().max(100_000);
// ----- self-service punch -----------------------------------------------------------------------------------------------------------
export const selfPunchPreviewSchema = z.object({
    direction: z.enum(SELF_PUNCH_DIRECTIONS).default('in'),
    channel: z.enum(SELF_PUNCH_CHANNELS).default('web'),
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    accuracy: accuracySchema.optional(),
    isMock: z.boolean().optional(),
}).refine((v) => (v.lat === undefined) === (v.lng === undefined), { message: 'lat and lng go together', path: ['lng'] });
/**
 * POST /orgs/:orgId/me/punch. The punch time is ALWAYS the server's clock: `clientQueuedAt` (when an offline punch was
 * queued on the device) is stored in the raw payload for information only. `idempotencyKey` makes a replay (the offline
 * queue re-sending, a double click) return the original punch instead of recording a second one.
 */
export const selfPunchSchema = z.object({
    direction: z.enum(SELF_PUNCH_DIRECTIONS),
    channel: z.enum(SELF_PUNCH_CHANNELS).default('web'),
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    accuracy: accuracySchema.optional(),
    isMock: z.boolean().optional(),
    clientQueuedAt: isoDateTimeSchema.optional(),
    idempotencyKey: z.string().trim().min(8).max(100).regex(/^[A-Za-z0-9_.:-]+$/, 'Letters, digits and _ . : - only'),
}).refine((v) => (v.lat === undefined) === (v.lng === undefined), { message: 'lat and lng go together', path: ['lng'] });
/** GET /orgs/:orgId/me/punch/status — `channel` decides which switch (web / mobile check-in) the blockers are computed for. */
export const selfPunchStatusQuerySchema = z.object({ channel: z.enum(SELF_PUNCH_CHANNELS).default('web') });
/** Why a self-service punch was refused (details.reason of the 403 / 409). */
export const SELF_PUNCH_REFUSALS = ['WEB_CHECKIN_DISABLED', 'MOBILE_CHECKIN_DISABLED', 'IP_NOT_ALLOWED', 'OUT_OF_WINDOW', 'OUTSIDE_GEOFENCE', 'MOCK_LOCATION', 'SELFIE_REQUIRED', 'DUPLICATE_PUNCH', 'ALREADY_CHECKED_IN', 'NOT_CHECKED_IN', 'PERIOD_LOCKED', 'NOT_ACTIVE',
    /** The employee's attendance policy (`policy.methods`, Enterprise) does not allow this channel (web / mobile / selfie). */
    'CHECKIN_METHOD_NOT_ALLOWED'];
// ----- selfie check-in ------------------------------------------------------------------------------------------------------------
export const SELFIE_MAX_BYTES = 2 * 1024 * 1024;
/** POST /orgs/:orgId/me/selfie-checkin as JSON (the multipart form carries the same fields plus a `photo` file). */
export const selfSelfieSchema = z.object({
    direction: z.enum(SELF_PUNCH_DIRECTIONS),
    /** JPEG / PNG / WebP, base64 or a data URL; at most 2 MB once decoded. */
    imageBase64: z.string().min(16).max(Math.ceil((SELFIE_MAX_BYTES * 4) / 3) + 64),
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    accuracy: accuracySchema.optional(),
    isMock: z.boolean().optional(),
}).refine((v) => (v.lat === undefined) === (v.lng === undefined), { message: 'lat and lng go together', path: ['lng'] });
/** The multipart form of the selfie check-in: the `photo` file plus these fields (form values arrive as strings). */
export const selfSelfieFormSchema = z.object({
    direction: z.enum(SELF_PUNCH_DIRECTIONS),
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    accuracy: z.coerce.number().positive().max(100_000).optional(),
    isMock: booleanQuerySchema.optional(),
}).refine((v) => (v.lat === undefined) === (v.lng === undefined), { message: 'lat and lng go together', path: ['lng'] });
export const selfieListQuerySchema = paginationQuerySchema.extend({ status: z.enum(SELFIE_CHECKIN_STATUSES).optional(), employeeId: uuidSchema.optional() });
export const selfieReviewSchema = z.object({ decision: z.enum(['approve', 'reject']), reason: z.string().trim().max(1000).optional() })
    .refine((v) => v.decision !== 'reject' || !!v.reason, { message: 'A reason is required when rejecting.', path: ['reason'] });
/** Withdrawing one's own regularisation / swap: an optional reason (a default is recorded when none is given). */
export const portalCancelSchema = z.object({ reason: z.string().trim().min(3).max(500).optional() });
// ----- attendance grants ----------------------------------------------------------------------------------------------------------
export const attendanceGrantsInputSchema = z.object({ openAttendance: z.boolean(), selfieRequired: z.boolean() });
// ----- attendance notes (per-day reasons) ----------------------------------------------------------------------------------------
export const selfNoteInputSchema = z.object({
    date: isoDateSchema,
    category: z.enum(ATTENDANCE_NOTE_CATEGORIES),
    note: z.string().trim().min(3, 'Explain the day in a few words').max(2000),
});
/** PATCH …/me/attendance/notes/:id — no defaults (a single-field edit never resets the other). */
export const selfNoteUpdateSchema = z.object({
    category: z.enum(ATTENDANCE_NOTE_CATEGORIES).optional(),
    note: z.string().trim().min(3).max(2000).optional(),
}).refine((v) => v.category !== undefined || v.note !== undefined, { message: 'Nothing to update' });
export const selfNotesQuerySchema = z.object({ from: isoDateSchema.optional(), to: isoDateSchema.optional() });
export const NOTE_LIST_SCOPES = ['mine', 'team', 'all'];
export const attendanceNotesQuerySchema = paginationQuerySchema.extend({
    /** mine = my direct reports' notes and notes routed to me by the approval engine; team = my direct reports; all = organisation-wide oversight. */
    scope: z.enum(NOTE_LIST_SCOPES).default('mine'),
    status: z.enum(ATTENDANCE_NOTE_STATUSES).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    employeeId: uuidSchema.optional(),
    /** Only the notes waiting for a review (pending / info requested). */
    open: booleanQuerySchema.optional(),
});
/**
 * POST /orgs/:orgId/attendance/notes/:id/review. A rejection names its pay effect; a question needs its text.
 * `onBehalfOfUserId` names the pending seat an organisation-wide reviewer's override (or an escalated reviewer's decision)
 * fills — REQUIRED when the note's approval level is ALL or QUORUM with several seats waiting (the approval request's
 * `abilities.mustChooseSeat`; 400 `APPROVAL_SEAT_CHOICE_MESSAGE` otherwise), exactly as on /approvals/:id/decide (engine §9.8).
 */
export const noteReviewSchema = z.object({
    decision: z.enum(NOTE_REVIEW_DECISIONS),
    reason: z.string().trim().max(1000).optional(),
    payEffectDays: z.union([z.literal(0), z.literal(0.5), z.literal(1)]).optional(),
    onBehalfOfUserId: uuidSchema.optional(),
}).superRefine((v, ctx) => {
    if (v.decision === 'reject' && v.payEffectDays === undefined)
        ctx.addIssue({ code: 'custom', path: ['payEffectDays'], message: 'Choose the pay effect of the rejection (none, half day, full day).' });
    if (v.decision !== 'reject' && v.payEffectDays !== undefined)
        ctx.addIssue({ code: 'custom', path: ['payEffectDays'], message: 'Only a rejection has a pay effect.' });
    if (v.decision === 'request_info' && !v.reason)
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Write the question for the employee.' });
});
// ----- regularisation ---------------------------------------------------------------------------------------------------------------
/** POST /orgs/:orgId/me/regularisations. Proposed times are instants (UTC); missed / wrong punch need at least one of them. */
export const selfRegularisationInputSchema = z.object({
    date: isoDateSchema,
    type: z.enum(REGULARISATION_TYPES),
    proposedInAt: isoDateTimeSchema.optional(),
    proposedOutAt: isoDateTimeSchema.optional(),
    reason: z.string().trim().min(3, 'Explain the request in a few words').max(1000),
}).superRefine((v, ctx) => {
    if ((v.type === 'missed_punch' || v.type === 'wrong_punch') && !v.proposedInAt && !v.proposedOutAt)
        ctx.addIssue({ code: 'custom', path: ['proposedInAt'], message: 'Give the check-in and / or check-out time.' });
    if (v.proposedInAt && v.proposedOutAt && Date.parse(v.proposedOutAt) <= Date.parse(v.proposedInAt))
        ctx.addIssue({ code: 'custom', path: ['proposedOutAt'], message: 'The check-out must be after the check-in.' });
});
export const selfRegularisationsQuerySchema = z.object({ from: isoDateSchema.optional(), to: isoDateSchema.optional(), status: z.enum(REGULARISATION_STATUSES).optional() });
export const selfShiftSwapInputSchema = z.object({
    date: isoDateSchema,
    withEmployeeId: uuidSchema,
    reason: z.string().trim().min(3, 'Explain the swap in a few words').max(1000),
});
export const swapCandidatesQuerySchema = z.object({ date: isoDateSchema, search: z.string().trim().max(100).optional() });
export const selfShiftSwapsQuerySchema = z.object({ status: z.enum(SHIFT_SWAP_STATUSES).optional() });
// ----- self statistics --------------------------------------------------------------------------------------------------------------
export const SELF_STATS_RANGES = ['30d', 'month', 'year'];
export const selfStatsQuerySchema = z.object({ range: z.enum(SELF_STATS_RANGES).default('30d') });
// ----- geofences (HR) ---------------------------------------------------------------------------------------------------------------
/** A weekly window in the branch's local time: ISO weekdays (1 = Monday … 7 = Sunday); may wrap midnight. */
export const geofenceTimeWindowSchema = z.object({ days: z.array(z.number().int().min(1).max(7)).min(1).max(7), start: timeSchema, end: timeSchema });
const polygonSchema = z.array(z.tuple([latitudeSchema, longitudeSchema])).min(3).max(100);
export const geofenceAssignmentInputSchema = z.object({
    scope: z.enum(GEOFENCE_SCOPES),
    targetId: uuidSchema.nullable().optional(),
    priority: z.number().int().min(0).max(1000).default(100),
    requireOnCheckIn: z.boolean().default(true),
    requireOnCheckOut: z.boolean().default(true),
}).superRefine((v, ctx) => {
    if (v.scope === 'org' && v.targetId)
        ctx.addIssue({ code: 'custom', path: ['targetId'], message: 'An organisation-wide assignment has no target.' });
    if (v.scope !== 'org' && !v.targetId)
        ctx.addIssue({ code: 'custom', path: ['targetId'], message: 'Choose who the fence applies to.' });
    if (!v.requireOnCheckIn && !v.requireOnCheckOut)
        ctx.addIssue({ code: 'custom', path: ['requireOnCheckIn'], message: 'The fence must apply to check-ins, check-outs or both.' });
});
const geofenceShape = {
    name: z.string().trim().min(1).max(120),
    branchId: uuidSchema.nullable().optional(),
    /** The place the fence outlines (a site, building… of its branch; docs/locations.md). Needs a branch; null clears it. */
    locationId: uuidSchema.nullable().optional(),
    latitude: latitudeSchema,
    longitude: longitudeSchema,
    radiusM: z.number().int().min(30).max(5000),
    polygon: polygonSchema.nullable().optional(),
    enforcement: z.enum(GEOFENCE_ENFORCEMENTS).default('soft_warn'),
    accuracyThresholdM: z.number().int().min(5).max(5000).default(100),
    graceM: z.number().int().min(0).max(1000).default(0),
    activeFrom: isoDateSchema.nullable().optional(),
    activeTo: isoDateSchema.nullable().optional(),
    timeWindows: z.array(geofenceTimeWindowSchema).max(14).nullable().optional(),
    isActive: z.boolean().default(true),
};
export const geofenceInputSchema = z.object({ ...geofenceShape, assignments: z.array(geofenceAssignmentInputSchema).max(200).optional() })
    .refine((v) => !v.activeFrom || !v.activeTo || v.activeTo >= v.activeFrom, { message: 'activeTo must be on/after activeFrom', path: ['activeTo'] });
/** PUT /orgs/:orgId/geofences/:id/assignments — replaces the fence's whole assignment list. */
export const geofenceAssignmentsInputSchema = z.object({ assignments: z.array(geofenceAssignmentInputSchema).max(200) });
/** PATCH body: no defaults (AGENTS.md Zod 4 pitfall — a rename must not reset enforcement / accuracy / grace / isActive). */
export const geofenceUpdateSchema = updateSchemaOf(geofenceShape);
export const geofenceListQuerySchema = z.object({ branchId: uuidSchema.optional(), locationId: uuidSchema.optional(), includeInactive: booleanQuerySchema.optional() });
export const geofenceEvaluateSchema = z.object({
    employeeId: uuidSchema,
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    accuracy: accuracySchema.optional(),
    isMock: z.boolean().optional(),
    direction: z.enum(SELF_PUNCH_DIRECTIONS).default('in'),
    /** Evaluate at this instant (default now) — time windows and active dates use the employee's branch timezone. */
    at: isoDateTimeSchema.optional(),
}).refine((v) => (v.lat === undefined) === (v.lng === undefined), { message: 'lat and lng go together', path: ['lng'] });
//# sourceMappingURL=portal-attendance.js.map